import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { combineLatest, Observable } from 'rxjs';
import { catchError, first, map, switchMap, tap } from 'rxjs/operators';
import { RoleMgr } from '../../core';
import { AppState } from '../../core/app-state';
import { AppTab } from '../../core/app-tab';
import { DriveTestPoint } from '../../core/models';
import { PathTestPoint } from '../../core/models/path-test';
import { DriveTestDoc } from '../../core/schema/drive-test-schema';
import { PathTestDoc } from '../../core/schema/path-test-schema';
import { BuildingDoc, BuildingService, DriveTestService, PathTestService, UserService } from '../../core/services';
import { ShowRequest } from '../../core/show-request';
import { FesBaseComponent } from '../../shared/components/fes-base.component';
import { PathTestEditComponent } from '../path-test-edit/path-test-edit.component';
import { PathTestPointListVisibleColumns } from '../path-test-point/path-test-point-list/path-test-point-list.component';


class PathTestRow {
  pathId: string
  cycle: number
  testPoints: PathTestPoint[] = []
  

  constructor( public doc: PathTestDoc | DriveTestDoc ){
    if ( doc ) {
      this.pathId = doc.pathId
      this.testPoints = doc.testPoints
      this.cycle = doc.cycle
    }
  }
}

//TBD Rename to BuildingTestStageComponent?  Or make this base class for that class
@Component({
  selector: 'app-path-test-group',
  templateUrl: './path-test-group.component.html',
  styleUrls: ['./path-test-group.component.scss'],
})
export class PathTestGroupComponent extends FesBaseComponent 
    implements OnInit, OnDestroy  {

  @Input() stage: string
  @Input() buildingId: string

  @Input('groupCriteriaJson') set groupCriteriaJsonSetter( json: string ) {
    //console.log(`groupCriteriaJson set(json:${json})`)
    const criteria = JSON.parse(json)    
    this.stage = criteria.stage 
    this.buildingId = criteria.buildingId
  }

  building$: Observable<BuildingDoc>
  pathTestRows$: Observable<PathTestRow[]>
  timezone: string
  visibleColumns: PathTestPointListVisibleColumns = { 
    callCounts: true, 
    time: true, 
    comments: true, 
    waypoint: false,
    seqStatus: true
  }

  @Input() set tabConfig( tabConf : AppTab ) { 
    tabConf.setSelected = this.onTabSelected
    this.closeTab = tabConf.close
  }

  closeTab : () => void


  constructor(
    appState : AppState,
    public pathTestService: PathTestService,
    public driveTestService: DriveTestService,
    public buildingService: BuildingService,
    public userService: UserService,
    public roleMgr: RoleMgr,
    public bsModalService: BsModalService ) {
    super(appState)
   
  }


  ngOnInit(): void {
    this.initTestPathQuery()
    this.building$ = this.buildingService.rxCollection.findOne( this.buildingId ).$
    //this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.timezone = new Date().toLocaleTimeString('en-us',{timeZoneName:'short'}).split(' ')[2]
  }


  ngOnDestroy(): void {
    this.destroy()
  }


  bTabActive = false
  initialRowsLoaded = false
  errorMsg = ""

  initTestPathQuery() {
    const query = this.pathTestService.rxCollection.find( { selector: { 
        $and: [ 
          { buildingId: this.buildingId },
          { stage: this.stage }
        ] 
      }
    })
    this.pathTestRows$ = query.$.pipe(
      switchMap( (docs: PathTestDoc[]) => 
        combineLatest(
          docs.map( doc => doc.$.pipe( map( (d : PathTestDoc) => new PathTestRow(d) )))
        )
      ),
      tap( docs => {
        this.initialRowsLoaded = true
        this.errorMsg = null        
      }),
      catchError( e => {
        this.errorMsg = e?.message || e
        this.initialRowsLoaded = true
        return [] 
      })
    )    
  }  
  initTestDriveQuery() {
    const query = this.driveTestService.rxCollection.find( { selector: { 
        $and: [ 
          { buildingId: this.buildingId },
          { stage: this.stage }
        ] 
      }
    })
    this.pathTestRows$ = query.$.pipe(
      switchMap( (docs: PathTestDoc[]) => 
        combineLatest(
          docs.map( doc => doc.$.pipe( map( (d : PathTestDoc) => new PathTestRow(d) )))
        )
      ),
      tap( docs => {
        this.initialRowsLoaded = true
        this.errorMsg = null        
      }),
      catchError( e => {
        this.errorMsg = e?.message || e
        this.initialRowsLoaded = true
        return [] 
      })
    )    
  }  

  unsubscribe() {
    super.unsubscribe()
  }


  onTabSelected = ( bSelected : boolean ) => {
    this.bTabActive = bSelected 
    if ( bSelected ) { 
      //this.initSubscriptions()
    } else {
      this.unsubscribe()
    }
  }

  showTestPath = async ( pathTestDoc: PathTestDoc ) => {
    // doc ref in html table gets removed when tab loses focus so get new ref
    pathTestDoc = await this.pathTestService.getById(pathTestDoc._id)
    this.appState.showView.next( new ShowRequest('tab','path test',pathTestDoc._id, pathTestDoc) )
  }

  onOpenPathEditDlg = async ( pathTestDoc: PathTestDoc ) => {
    pathTestDoc = await this.pathTestService.getById(pathTestDoc._id)
    const initialState = {
      pathTestDoc,
      doAfterSaveFn: () => {        
      }
    }
    const modalRef = this.bsModalService.show(PathTestEditComponent, {ignoreBackdropClick: true, initialState});
    const component: PathTestEditComponent = modalRef.content
    const sub = component.deleted$.subscribe( doc => {
      this.appState.notifySuccess('Path test deleted for path ' + doc.pathId,this,3)      
      modalRef.hide()
      this.closeTab && this.closeTab()
    })
    await modalRef.onHidden.pipe(first()).toPromise()
    sub.unsubscribe()
  }

  onDeletePathTest = async ( pathTestDoc: PathTestDoc ) => {
    pathTestDoc = await this.pathTestService.getById(pathTestDoc._id)
    await this.pathTestService.deletePathTestModal(this.bsModalService,pathTestDoc)
  }

}
